import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Clock, MapPin, Video, Users, UserCheck,
  Paperclip, Bell, Activity, Edit3, ExternalLink, Copy, Globe, ClipboardCheck,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/PageHeader';
import { calendarAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { formatEventDateTime } from '@/lib/eventTime';
import EventCover from '@/components/calendar/EventCover';
import useInternalCoverBlob from '@/components/calendar/useInternalCoverBlob';
import LifecycleActions from '@/components/calendar/LifecycleActions';
import AddToCalendar from '@/components/calendar/AddToCalendar';
import AttachmentsPanel from '@/components/calendar/AttachmentsPanel';
import ParticipantsPanel from '@/components/calendar/ParticipantsPanel';
import CheckInPanel from '@/components/calendar/CheckInPanel';
import NotificationsPanel from '@/components/calendar/NotificationsPanel';
import ActivityPanel from '@/components/calendar/ActivityPanel';
import ShareLinkPanel from '@/components/calendar/ShareLinkPanel';
import EventFormDrawer from '@/components/calendar/EventFormDrawer';
import EventSlotsPanel from '@/components/calendar/EventSlotsPanel';
import {
  eventTypeLabel, businessCategoryLabel, languageLabel, VISIBILITY_SCOPES,
  wizardTypeByCategory,
} from '@/components/calendar/eventFormHelpers';

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
  completed: 'bg-sky-100 text-sky-700 border-sky-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

const scopeLabel = (v) =>
  VISIBILITY_SCOPES.find((s) => s.value === v)?.label?.split(' — ')[0] || v || '—';

const KV = ({ label, value, testId }) => (
  <div className="flex flex-col gap-0.5" data-testid={testId}>
    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-sm text-slate-800">{value ?? <span className="text-slate-400">—</span>}</span>
  </div>
);

// Phase 27.30.20 — Auth-safe cover thumb for the workspace header.
// The internal cover route requires a Bearer token, so a plain
// <img src="/api/.../cover"> always fails and falls back to the
// placeholder. Route the URL through the shared blob hook that the
// calendar grid + list already use.
function WorkspaceCover({ event }) {
  const url = useInternalCoverBlob(event?.cover_image_url);
  return (
    <EventCover
      coverUrl={url}
      title={event?.title || 'Event'}
      category={event?.category}
      variant="thumb"
    />
  );
}

/**
 * Phase 27.30.11 — Event Workspace (Wix-style tabs).
 *
 * Replaces the "all-stacked" drawer feel with a full page that mirrors
 * how the Accounting module organises its work: a persistent header
 * (cover, title, status, actions) + top tabs that let the user focus on
 * one concern at a time — Overview, Schedule, Location, Audience,
 * Attachments, Notifications, Activity.
 *
 * Phase 1 (this iteration) implements Overview + Schedule + Location as
 * dedicated panels. The remaining tabs render the existing panel
 * components so nothing breaks while later phases refine each one.
 */
export default function EventWorkspacePage() {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isKR = isKRRole(user);

  const [tab, setTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [regSummary, setRegSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const refetch = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [ev, parts, atts, reg] = await Promise.all([
        calendarAPI.getEvent(eventId, { include_archived: true }).then((r) => r.data),
        calendarAPI.listParticipants(eventId).then((r) => r.data).catch(() => []),
        calendarAPI.listAttachments(eventId).then((r) => r.data.items || []).catch(() => []),
        calendarAPI.registrationSummary(eventId).then((r) => r.data).catch(() => null),
      ]);
      setDetail(ev);
      setParticipants(parts.items || parts || []);
      setAttachments(atts);
      setRegSummary(reg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load event');
      navigate('/calendar');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => { refetch(); }, [refetch]);

  useEffect(() => {
    calendarAPI.categories()
      .then((r) => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, []);

  const doLifecycle = async (action, payload = {}) => {
    try {
      if (action === 'publish') await calendarAPI.publish(eventId);
      else if (action === 'cancel') await calendarAPI.cancel(eventId, payload?.reason);
      else if (action === 'archive') await calendarAPI.archive(eventId, payload?.reason);
      else if (action === 'complete') await calendarAPI.complete(eventId);
      toast.success(`Event ${action}d`);
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
  };

  if (loading || !detail) {
    return (
      <div className="p-8 text-center text-slate-500">Loading event workspace…</div>
    );
  }

  const canManage = isKR || detail.created_by_user_id === user?.user_id;
  // Phase 1b — tabs follow the event type: registration & attachments are
  // hidden for types that don't use them (e.g. KR Internal Meeting). Other
  // tabs are always relevant. Legacy/ambiguous category → show everything.
  const wtype = wizardTypeByCategory(detail.category);
  const showSlots = Boolean((wtype && wtype.flags && wtype.flags.slots) || detail.slots_enabled);
  const showTab = (id) => {
    if (id === 'slots') return showSlots;
    if (!wtype) return true;
    if (id === 'registration') return (wtype.sections || []).includes('registration');
    if (id === 'attachments') return (wtype.sections || []).includes('attachments');
    return true;
  };
  const locStr = [
    detail.physical_location?.street,
    detail.physical_location?.city,
    detail.physical_location?.state,
    detail.physical_location?.zip,
  ].filter(Boolean).join(', ');
  const isPublic = detail.visibility_scope === 'public' && detail.status === 'published';
  const publicUrl = isPublic
    ? `${window.location.origin}/events/${detail.event_id}`
    : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-workspace-page">
      <PageHeader
        title=""
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="workspace-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        }
      />

      {/* Header band: cover + title + status + primary actions */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col md:flex-row gap-5 items-start">
          <div className="w-full md:w-56 shrink-0">
            <WorkspaceCover event={detail} />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`border ${STATUS_TONE[detail.status] || STATUS_TONE.draft}`}>
                {detail.status}
              </Badge>
              {detail.event_type && (
                <Badge variant="outline" className="text-xs">{eventTypeLabel(detail.event_type)}</Badge>
              )}
              {detail.business_category && (
                <Badge variant="outline" className="text-xs">{businessCategoryLabel(detail.business_category)}</Badge>
              )}
              <span className="text-xs text-slate-500">
                {formatEventDateTime(detail.start_datetime, detail.timezone)}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight" data-testid="workspace-title">
              {detail.title}
            </h1>
            {detail.description && (
              <p className="text-sm text-slate-600 line-clamp-2 max-w-3xl">{detail.description}</p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {canManage && (
                <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="workspace-edit-btn">
                  <Edit3 className="h-4 w-4 mr-1" /> Edit
                </Button>
              )}
              {canManage && (
                <LifecycleActions
                  status={detail.status}
                  publicationReady={true}
                  onAction={doLifecycle}
                />
              )}
              {publicUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(publicUrl);
                    toast.success('Public link copied');
                  }}
                  data-testid="workspace-copy-public-link"
                >
                  <Copy className="h-4 w-4 mr-1" /> Copy public link
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 py-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-5 flex flex-wrap h-auto" data-testid="workspace-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">
              <Clock className="h-3.5 w-3.5 mr-1.5" /> Schedule
            </TabsTrigger>
            <TabsTrigger value="location" data-testid="tab-location">
              <MapPin className="h-3.5 w-3.5 mr-1.5" /> Location
            </TabsTrigger>
            <TabsTrigger value="audience" data-testid="tab-audience">
              <Users className="h-3.5 w-3.5 mr-1.5" /> Audience
              <Badge variant="outline" className="ml-1.5 text-[10px]">{participants.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="checkin" data-testid="tab-checkin">
              <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" /> Check-in
            </TabsTrigger>
            {showTab('slots') && (
            <TabsTrigger value="slots" data-testid="tab-slots">
              <Clock className="h-3.5 w-3.5 mr-1.5" /> Slots
            </TabsTrigger>
            )}
            {showTab('registration') && (
            <TabsTrigger value="registration" data-testid="tab-registration">
              <UserCheck className="h-3.5 w-3.5 mr-1.5" /> Registration
            </TabsTrigger>
            )}
            {showTab('attachments') && (
            <TabsTrigger value="attachments" data-testid="tab-attachments">
              <Paperclip className="h-3.5 w-3.5 mr-1.5" /> Attachments
              {attachments.length > 0 && (
                <Badge variant="outline" className="ml-1.5 text-[10px]">{attachments.length}</Badge>
              )}
            </TabsTrigger>
            )}
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="h-3.5 w-3.5 mr-1.5" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">
              <Activity className="h-3.5 w-3.5 mr-1.5" /> Activity
            </TabsTrigger>
          </TabsList>

          {/* ── Overview ────────────────────────────────── */}
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardContent className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <KV label="Event Type" value={detail.event_type ? eventTypeLabel(detail.event_type) : null} testId="ov-event-type" />
                  <KV label="Business Category" value={detail.business_category ? businessCategoryLabel(detail.business_category) : null} testId="ov-business-category" />
                  <KV label="Language" value={detail.language ? languageLabel(detail.language) : null} testId="ov-language" />
                  <KV label="Category (legacy)" value={detail.category} testId="ov-category" />
                  <KV label="Carrier" value={detail.carrier_id} testId="ov-carrier" />
                  <KV label="Visibility" value={scopeLabel(detail.visibility_scope)} testId="ov-visibility" />
                  <KV label="Requires RSVP" value={detail.requires_rsvp ? 'Yes' : 'No'} testId="ov-rsvp" />
                  <KV label="All-day" value={detail.all_day ? 'Yes' : 'No'} testId="ov-allday" />
                  <KV label="Capacity" value={detail.capacity ?? 'Unlimited'} testId="ov-capacity" />
                </div>
              </CardContent>
            </Card>
            {detail.description && (
              <Card>
                <CardContent className="p-5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Description</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="ov-description">{detail.description}</p>
                </CardContent>
              </Card>
            )}
            {/* Phase 27.35 — For PUBLIC events the "Copy public link" at
                the top already serves as the single share link (and now
                enforces NPN when required). We hide the ShareLinkPanel
                to avoid giving the admin two separate URLs. Private
                events still need it since /events/{id} is not exposed. */}
            {detail.visibility_scope === 'public' ? (
              publicUrl ? (
                <Card data-testid="public-link-info-card">
                  <CardContent className="p-5 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-slate-800">Share link</div>
                      {detail.requires_npn_to_register && (
                        <Badge variant="outline" className="text-amber-800 bg-amber-50 border-amber-200 text-[10px]">
                          NPN required
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">
                      This event is public. Use the <span className="font-medium">Copy public link</span> button at
                      the top of this page — the same link handles registrations{' '}
                      {detail.requires_npn_to_register
                        ? 'and enforces NPN. Recognised NPNs join as agents; unrecognised NPNs are added to Prospects for recruiting.'
                        : 'without a login. No separate share link needed.'}
                    </p>
                    <div className="flex gap-2 pt-1">
                      <Input readOnly value={publicUrl} className="text-xs font-mono" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Public link copied'); }}
                        data-testid="overview-copy-public-link"
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-500">
                      Publish this event to generate its public link. Public events
                      use one link — no separate share link is created.
                    </p>
                  </CardContent>
                </Card>
              )
            ) : (
              <ShareLinkPanel event={detail} canManage={canManage} onRefresh={refetch} />
            )}
          </TabsContent>

          {/* ── Schedule ────────────────────────────────── */}
          <TabsContent value="schedule" className="space-y-4">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <KV
                    label="Starts"
                    value={formatEventDateTime(detail.start_datetime, detail.timezone)}
                    testId="sch-starts"
                  />
                  <KV
                    label="Ends"
                    value={formatEventDateTime(detail.end_datetime, detail.timezone)}
                    testId="sch-ends"
                  />
                  <KV label="Timezone" value={detail.timezone} testId="sch-timezone" />
                  <KV label="All-day" value={detail.all_day ? 'Yes' : 'No'} testId="sch-allday" />
                  {detail.registration_deadline && (
                    <KV
                      label="Registration deadline"
                      value={formatEventDateTime(detail.registration_deadline, detail.timezone)}
                      testId="sch-reg-deadline"
                    />
                  )}
                </div>
                {detail.status !== 'draft' && detail.status !== 'archived' && (
                  <div className="pt-3 border-t border-slate-100">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Add this event to your calendar
                    </div>
                    <AddToCalendar variant="internal" eventId={detail.event_id} />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Location ────────────────────────────────── */}
          <TabsContent value="location" className="space-y-4">
            <Card>
              <CardContent className="p-5 space-y-4">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> Physical location
                  </div>
                  {locStr ? (
                    <p className="text-sm text-slate-700" data-testid="loc-address">{locStr}</p>
                  ) : (
                    <p className="text-sm text-slate-400">No physical address on file.</p>
                  )}
                  {detail.physical_location?.room && (
                    <p className="text-xs text-slate-500 mt-1">Room: {detail.physical_location.room}</p>
                  )}
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <Video className="h-3.5 w-3.5" /> Virtual meeting
                  </div>
                  {detail.virtual_meeting_url ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <a
                        href={detail.virtual_meeting_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sky-600 hover:underline text-sm break-all"
                        data-testid="loc-meeting-url"
                      >
                        {detail.virtual_meeting_url}
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(detail.virtual_meeting_url);
                          toast.success('Meeting link copied');
                        }}
                        data-testid="loc-copy-meeting-url"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(detail.virtual_meeting_url, '_blank', 'noopener')}
                        data-testid="loc-open-meeting-url"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No virtual meeting link.</p>
                  )}
                  {detail.virtual_meeting_url && !detail.expose_meeting_url_publicly && (
                    <p className="text-[11px] text-slate-500 mt-2">
                      The meeting link is hidden from the public listing (only assigned participants see it).
                    </p>
                  )}
                </div>
                {publicUrl && (
                  <div className="pt-3 border-t border-slate-100">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5" /> Public share
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/events/${detail.event_id}`}
                        className="text-sky-600 hover:underline text-sm break-all"
                        target="_blank"
                        data-testid="loc-public-link"
                      >
                        {publicUrl}
                      </Link>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(publicUrl);
                          toast.success('Public link copied');
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Audience (Phase 2 placeholder — reuses existing panel) ────── */}
          <TabsContent value="audience">
            <Card>
              <CardContent className="p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Audience &amp; Participants</div>
                    <div className="text-xs text-slate-500">Visibility: <b>{scopeLabel(detail.visibility_scope)}</b></div>
                  </div>
                </div>
                <ParticipantsPanel
                  event={detail}
                  participants={participants}
                  currentUser={user}
                  canManage={canManage}
                  onChanged={refetch}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Check-in (Phase 2 — dedicated day-of-event workflow) ────── */}
          <TabsContent value="checkin">
            <CheckInPanel
              event={detail}
              participants={participants}
              canManage={canManage}
              onChanged={refetch}
            />
          </TabsContent>

          {/* ── Slots (Phase 2) ── */}
          {showTab('slots') && (
            <TabsContent value="slots">
              <EventSlotsPanel eventId={detail.event_id} timezone={detail.timezone} onConfigChanged={refetch} />
            </TabsContent>
          )}

          {/* ── Registration (basic summary — full panel arrives in Phase 2) ── */}
          <TabsContent value="registration">
            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Registration summary
                </div>
                {regSummary ? (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    <KV label="Invited" value={regSummary.counts?.invited ?? 0} testId="reg-invited" />
                    <KV label="Confirmed" value={regSummary.registered_count ?? 0} testId="reg-confirmed" />
                    <KV label="Waitlist" value={regSummary.counts?.waitlisted ?? 0} testId="reg-waitlist" />
                    <KV label="Cancelled" value={regSummary.counts?.cancelled ?? 0} testId="reg-cancelled" />
                    <KV
                      label="Remaining seats"
                      value={regSummary.remaining_seats ?? (detail.capacity != null
                        ? Math.max(0, (detail.capacity || 0) - (regSummary.registered_count || 0))
                        : 'Unlimited')}
                      testId="reg-remaining"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No registrations yet.</p>
                )}
                <p className="text-[11px] text-slate-500">
                  Full registration controls (invite, RSVP, cancel) will move into this tab in Phase 2.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Attachments ────────────────────────────────── */}
          <TabsContent value="attachments">
            <Card>
              <CardContent className="p-5">
                <AttachmentsPanel
                  event={detail}
                  attachments={attachments}
                  canManage={canManage}
                  isKR={isKR}
                  onChanged={refetch}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Notifications ────── */}
          <TabsContent value="notifications">
            <NotificationsPanel event={detail} canManage={canManage} />
          </TabsContent>

          {/* ── Activity ────── */}
          <TabsContent value="activity">
            <ActivityPanel event={detail} canManage={canManage} />
          </TabsContent>
        </Tabs>
      </div>

      <EventFormDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        event={detail}
        mode="edit"
        categories={categories}
        onSaved={() => { setEditOpen(false); refetch(); }}
      />
    </div>
  );
}
